import { useRef, useState } from 'react'
import { GCC_TAXONOMY_EXTS, gccTaxonomyRejection } from '@shared/gcc/uploads'
import GccTaxonomyHelp from './GccTaxonomyHelp'
import GccTaxonomySizeNotice from './GccTaxonomySizeNotice'

const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Technology', 'Manufacturing',
  'Retail', 'Energy', 'Consumer Goods', 'Telecommunications',
  'Professional Services', 'Other',
]

const GCC_COLOR = '#059669'

interface Props {
  clientName: string; setClientName: (v: string) => void
  industry: string; setIndustry: (v: string) => void
  uploadedFiles: Array<{ base64: string; name: string }>
  setUploadedFiles: (v: Array<{ base64: string; name: string }>) => void
  primaryColor: string; setPrimaryColor: (c: string) => void
  onBack: () => void
  onNext: () => void
}

export default function GccStep1_ClientDetails({
  clientName, setClientName,
  industry, setIndustry,
  uploadedFiles, setUploadedFiles,
  primaryColor, setPrimaryColor,
  onBack, onNext,
}: Props) {
  const [uploading, setUploading] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const [hexInput, setHexInput] = useState(primaryColor)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /** Shared by the picker and drag & drop (WI 2345) so both apply the same rules. */
  const addFiles = async (files: FileList | File[]) => {
    // A second batch while one is still being read would append to a stale uploadedFiles.
    if (uploading || files.length === 0) return
    setUploading(true)
    const added: Array<{ base64: string; name: string }> = []
    const rejections: string[] = []
    for (const file of Array.from(files)) {
      // `accept` is only a picker hint — "All files" lets anything through (WI 2332).
      const rejection = gccTaxonomyRejection(file.name, file.size)
      if (rejection) { rejections.push(rejection); continue }
      const buf = await file.arrayBuffer()
      const b64 = btoa(Array.from(new Uint8Array(buf), b => String.fromCharCode(b)).join(''))
      added.push({ base64: b64, name: file.name })
    }
    if (added.length > 0) setUploadedFiles([...uploadedFiles, ...added])
    setUploadErrors(rejections)
    setUploading(false)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return
    await addFiles(e.target.files)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // preventDefault on dragover is what makes the div a drop target; on drop it
  // stops the browser (or Electron window) from opening the file instead.
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    void addFiles(e.dataTransfer.files)
  }

  const canContinue = clientName.trim().length > 0 && industry !== '' && uploadedFiles.length > 0

  return (
    <div className="max-w-2xl mx-auto px-8 py-8 space-y-6">
      {/* Section header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Client Details</h2>
        <p className="text-sm text-gray-500 mt-1">
          Enter the client name and industry. These drive the GCC eligibility analysis and workforce modelling.
        </p>
      </div>

      {/* Client Name */}
      <section>
        <label htmlFor="gcc-client-name" className="block text-sm font-semibold text-gray-700 mb-2">Client Name</label>
        <input
          id="gcc-client-name"
          type="text"
          value={clientName}
          onChange={e => setClientName(e.target.value)}
          placeholder="e.g. Acme Corporation"
          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
        />
      </section>

      {/* Industry */}
      <section>
        <label htmlFor="gcc-industry" className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
        <select
          id="gcc-industry"
          value={industry}
          onChange={e => setIndustry(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 bg-white"
        >
          <option value="">Select an industry...</option>
          {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
        </select>
      </section>

      {/* Client Taxonomy (required) */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <label className="block text-sm font-semibold text-gray-700">Client Taxonomy</label>
          <span className="text-xs font-semibold text-red-500">required</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Upload the client's job architecture or process taxonomy. The engine will ground the task register in their
          actual roles, process naming, and org structure. MAVIM integration (automatic pull) is not yet available.
        </p>
        <GccTaxonomyHelp />

        {/* Upload dropzone */}
        <div
          data-testid="gcc-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={(e) => {
            // dragleave also fires when moving onto a child (the label); only clear on a real exit.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
          }}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-5 transition-colors ${
            dragging ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-300'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={GCC_TAXONOMY_EXTS.join(',')}
            onChange={handleFileChange}
            className="hidden"
            id="gcc-file-input"
          />
          <label
            htmlFor="gcc-file-input"
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-400 rounded-lg text-sm font-medium text-emerald-600 cursor-pointer hover:bg-emerald-50 transition-colors"
          >
            📎 {uploading ? 'Reading files…' : 'Choose file...'}
          </label>
          <span className="ml-2 text-sm text-gray-500">or drag &amp; drop files here</span>
          <p className="text-xs text-gray-400 mt-2">PDF, DOCX, PPTX, XLSX — max 20 MB</p>
          {uploadErrors.length > 0 && (
            <div role="alert" className="mt-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 space-y-0.5">
              {uploadErrors.map((msg, i) => <p key={i}>{msg}</p>)}
            </div>
          )}
          {uploadedFiles.length > 0 && (
            <ul className="mt-3 space-y-1">
              {uploadedFiles.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-emerald-50 rounded-lg px-3 py-2">
                  <span>📎</span>
                  <span className="truncate flex-1">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setUploadedFiles(uploadedFiles.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-400 flex-shrink-0 text-base leading-none"
                    aria-label={`Remove ${f.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <GccTaxonomySizeNotice files={uploadedFiles} />
        </div>

        {/* MAVIM placeholder */}
        <div className="mt-3 flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100 opacity-50 cursor-not-allowed select-none">
          <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
            M
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">MAVIM industry default</span>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-500 px-2 py-0.5 rounded">
                COMING SOON
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Pull the industry-standard operating model directly from MAVIM. Requires MAVIM API
              integration — not yet available.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Colour */}
      <section>
        <label className="block text-sm font-semibold text-gray-700 mb-3">Brand Colour</label>
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={primaryColor}
            onChange={e => { setPrimaryColor(e.target.value); setHexInput(e.target.value) }}
            className="w-12 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <input
            type="text"
            value={hexInput}
            onChange={e => {
              setHexInput(e.target.value)
              if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) setPrimaryColor(e.target.value)
            }}
            placeholder="#059669"
            className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-200"
            maxLength={7}
          />
          <span className="text-xs text-gray-400">Used for the GCC dashboard accent colour.</span>
        </div>
      </section>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-100">
        <button
          onClick={onBack}
          className="px-6 py-2.5 rounded-lg text-gray-600 font-semibold text-sm border border-gray-200 hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          className={`px-6 py-2.5 rounded-lg text-white font-semibold text-sm transition-all ${
            canContinue ? 'hover:opacity-90' : 'opacity-40 cursor-not-allowed'
          }`}
          style={{ background: canContinue ? GCC_COLOR : '#9CA3AF' }}
        >
          Next: GCC Config →
        </button>
      </div>
    </div>
  )
}
